import { take, call, put } from 'redux-saga/effects'
import * as types from '../../../../lib/actionTypes.es6'
import * as actions from './actions'
import {
  getOmkGroupsList,
  getOmkGroup,
  approveOmkGroup,
  onboardOmkGroupPolicies,
  getOmkGroupPolicyStatus,
  setOmkGroupVerification,
  getOmkGroupUploadUrl,
  postOmkGroupDocumentToS3,
  addOmkGroupDocument,
  deleteOmkGroupDocument,
  getCurrentGroupPolicies,
  policyActions,
  decideMakerCheck,
  policyNotes
} from '../../../../lib/requests.es6'
import {
  approvablePolicies,
  approvalPlanFor,
  rejectionPlanFor,
  policyLabel,
  readPolicyList,
  readPolicyTotal
} from '../lib/groupPolicyApproval'
import { humaniseError } from '../lib/errorCopy'
import { GAloggerException } from '../../../../lib/monitoring.es6'
import {
  unAuthorizedNotification,
  getUserFullname,
  getUserGuid
} from '../../../../lib/access.es6'

// Pulls the raw string out of an axios error, in the shapes the group service
// returns. Never let this throw — a throw inside a watcher's catch block kills
// the watcher for the rest of the session.
function rawMessageOf(e) {
  const data = e && e.response ? e.response.data : null
  if (data) {
    if (typeof data === 'string') return data
    if (data.message) {
      return Array.isArray(data.message)
        ? data.message.join('; ')
        : data.message
    }
  }
  if (e && e.message) return e.message
  return 'An unexpected error occurred'
}

// The message that actually reaches the operator: the raw backend string with
// UUIDs and internal field names scrubbed to approved copy. (BUG-10)
export function errorMessageOf(e) {
  return humaniseError(rawMessageOf(e))
}

// Every watcher below is take → call → success, with the same failure
// handling; this keeps that shape in one place.
function* watchRequest(requestType, handler, failureAction) {
  for (;;) {
    const request = yield take(requestType)
    try {
      yield call(handler, request.payload)
    } catch (e) {
      yield put(failureAction(errorMessageOf(e)))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* getOmkGroupsRequestWatcher() {
  yield call(
    watchRequest,
    types.GET_OMK_GROUPS_REQUEST,
    function*(payload) {
      const response = yield call(getOmkGroupsList, payload)
      yield put(actions.getOmkGroupsSuccess(response.data))
    },
    actions.getOmkGroupsFailure
  )
}

export function* getOmkGroupRequestWatcher() {
  yield call(
    watchRequest,
    types.GET_OMK_GROUP_REQUEST,
    function*(payload) {
      const response = yield call(getOmkGroup, payload)
      yield put(actions.getOmkGroupSuccess(response.data))
    },
    actions.getOmkGroupFailure
  )
}

// How long to wait for issuance to finish before letting the user get on with
// it. Onboarding is queued work, so "not done yet" is a normal outcome, not a
// failure — the panel says so rather than pretending it failed.
const ISSUE_POLL_INTERVAL_MS = 4000
const ISSUE_POLL_ATTEMPTS = 30

export function pause(ms) {
  return new Promise(function(resolve) {
    setTimeout(resolve, ms)
  })
}

// Approving a group used to leave the facilitator to go and issue the members'
// policies by hand. Approve only advances the group to Endorsed; issuance is a
// separate call, so it is chained here and the group is never left approved
// but un-issued because someone forgot the second step.
//
// Deliberately after the approve SUCCESS is dispatched: the approval itself has
// already happened and must be reported as done even if issuance then fails.
// An issuance failure is surfaced on its own, not as a failed approval.
export function* issueGroupPolicies(guid) {
  yield put(actions.issueOmkGroupPoliciesStarted())
  const response = yield call(onboardOmkGroupPolicies, guid, {
    // Skip members who fail validation instead of aborting the whole batch —
    // otherwise one bad member silently leaves the entire group un-issued.
    // The skipped count is surfaced so they can be chased.
    skip_invalid_members: true
  })
  const body = response.data || {}
  yield put(
    actions.issueOmkGroupPoliciesQueued({
      queued: readCount(body, ['queued', 'total_jobs', 'total', 'jobs_queued']),
      skipped: readCount(body, ['skipped', 'skipped_count'])
    })
  )

  // Issuance is asynchronous. Poll until the group reports every policy
  // registered, then let the caller refresh.
  //
  // Status is checked BEFORE the first wait: a small group can finish before
  // we would otherwise have looked, and waiting first would sit on a spinner
  // for no reason.
  for (let attempt = 0; attempt < ISSUE_POLL_ATTEMPTS; attempt++) {
    const status = yield call(getOmkGroupPolicyStatus, guid)
    if (status.data && status.data.policies_registered) {
      yield put(actions.issueOmkGroupPoliciesRegistered())
      return
    }
    yield call(pause, ISSUE_POLL_INTERVAL_MS)
  }
  yield put(actions.issueOmkGroupPoliciesPending())
}

// The onboarding response's field names are not in the swagger — only the
// prose "returns total jobs queued and skipped count" — so accept the likely
// spellings rather than showing a blank.
function readCount(body, names) {
  for (let i = 0; i < names.length; i++) {
    if (typeof body[names[i]] === 'number') {
      return body[names[i]]
    }
  }
  return null
}

export function* approveOmkGroupRequestWatcher() {
  yield call(
    watchRequest,
    types.APPROVE_OMK_GROUP_REQUEST,
    function*(payload) {
      const response = yield call(approveOmkGroup, payload)
      yield put(actions.approveOmkGroupSuccess(response.data))
      try {
        yield call(issueGroupPolicies, payload)
      } catch (e) {
        // The group IS approved; only issuance failed. Report it as such so
        // the operator knows to retry issuance rather than the approval.
        yield put(actions.issueOmkGroupPoliciesFailure(errorMessageOf(e)))
        GAloggerException(e)
      }
    },
    actions.approveOmkGroupFailure
  )
}

export function* setOmkGroupVerificationRequestWatcher() {
  yield call(
    watchRequest,
    types.SET_OMK_GROUP_VERIFICATION_REQUEST,
    function*(payload) {
      const response = yield call(
        setOmkGroupVerification,
        payload.guid,
        payload.verificationType,
        { status: payload.status, notes: payload.notes }
      )
      yield put(actions.setOmkGroupVerificationSuccess(response.data))
    },
    actions.setOmkGroupVerificationFailure
  )
}

// payload: { guid, name, file }. Presigned URL → S3 → record the bucket key.
// The record call answers with the whole updated group, so the store gets the
// new document list without a refetch.
export function* uploadOmkGroupDocumentRequestWatcher() {
  yield call(
    watchRequest,
    types.UPLOAD_OMK_GROUP_DOCUMENT_REQUEST,
    function*(payload) {
      const signedResponse = yield call(
        getOmkGroupUploadUrl,
        payload.guid,
        payload.file.name
      )
      const signed = signedResponse.data || {}
      if (!signed.signedUrl) {
        throw new Error('Upload URL missing from the response')
      }
      yield call(
        postOmkGroupDocumentToS3,
        signed.signedUrl,
        signed.fields,
        payload.file
      )
      const bucketKey =
        signed.bucketKey || (signed.fields ? signed.fields.key : null)
      if (!bucketKey) {
        throw new Error('Upload URL missing the bucket key')
      }
      const response = yield call(addOmkGroupDocument, payload.guid, {
        name: payload.name,
        url: bucketKey
      })
      yield put(actions.uploadOmkGroupDocumentSuccess(response.data))
    },
    actions.uploadOmkGroupDocumentFailure
  )
}

// Rows per page when the caller doesn't say. Mirrors the panel's default.
export const DEFAULT_PAGE_SIZE = 20

// How many rows to ask for per request while collecting every page.
const FETCH_ALL_PAGE_SIZE = 100

// Backstop so a service that ignores `page` (and therefore keeps returning the
// same first page) can never spin forever. 200 pages is far beyond any real
// group; hitting it means the endpoint is not paging and we stop with what we
// have rather than hanging the tab.
const FETCH_ALL_MAX_PAGES = 200

// Collect EVERY member policy of a group, across all pages.
//
// Only used for the whole-group approve, never for the table: approving "all"
// must mean all, and a group larger than one page would otherwise leave the
// policies behind the current page silently unapproved.
export function* fetchAllGroupPolicies(guid) {
  let policies = []
  let total = 0
  let page = 1

  for (; page <= FETCH_ALL_MAX_PAGES; page++) {
    const response = yield call(getCurrentGroupPolicies, {
      groupId: guid,
      page,
      limit: FETCH_ALL_PAGE_SIZE
    })
    const batch = readPolicyList(response.data)
    if (page === 1) {
      total = readPolicyTotal(response.data, batch.length)
    }
    // An empty page means we've run off the end — stop regardless of `total`,
    // which protects us if the reported count disagrees with what's served.
    if (!batch.length) {
      break
    }
    policies = policies.concat(batch)
    if (policies.length >= total) {
      break
    }
  }

  return { policies, total: Math.max(total, policies.length) }
}

// payload: { guid, page, limit } — one page for the table. `page` is 1-based.
export function* getOmkGroupPoliciesRequestWatcher() {
  yield call(
    watchRequest,
    types.GET_OMK_GROUP_POLICIES_REQUEST,
    function*(payload) {
      const page = payload.page || 1
      const limit = payload.limit || DEFAULT_PAGE_SIZE
      const response = yield call(getCurrentGroupPolicies, {
        groupId: payload.guid,
        page,
        limit
      })
      const policies = readPolicyList(response.data)
      yield put(
        actions.getOmkGroupPoliciesSuccess({
          policies,
          total: readPolicyTotal(response.data, policies.length),
          page,
          limit
        })
      )
    },
    actions.getOmkGroupPoliciesFailure
  )
}

// Run a decision — approve or reject — over member policies, one at a time.
//
// Deliberately sequential, and deliberately per-policy fault isolated: the
// group service has no bulk endpoint, so this is N calls, and one policy the
// server refuses (a maker-checker separation-of-duties 409, say) must not
// abort the policies queued behind it. Every outcome — success or failure — is
// reported so the UI can show exactly which rows failed and why, rather than a
// single "something went wrong".
export function* runGroupPolicyApproval(payload) {
  const rejecting = payload.decision === 'reject'

  // scope 'all' means every eligible policy in the GROUP, not just the page on
  // screen. The table is paginated, so the visible rows are not the whole set —
  // collect every page first, otherwise "all" would quietly mean "this page"
  // and leave the rest behind.
  let policies
  if (payload.scope === 'all') {
    const everything = yield call(fetchAllGroupPolicies, payload.guid)
    policies = approvablePolicies(everything.policies)
  } else {
    policies = Array.isArray(payload.policies) ? payload.policies : []
  }

  yield put(actions.approveOmkGroupPoliciesStarted(policies.length))

  for (let i = 0; i < policies.length; i++) {
    const policy = policies[i]
    const plan = rejecting
      ? rejectionPlanFor(policy, payload.reason)
      : approvalPlanFor(policy)
    let result
    try {
      if (plan.kind === 'maker-check') {
        yield call(decideMakerCheck, { guid: plan.guid, body: plan.body })
      } else {
        yield call(policyActions, {
          policy_guid: plan.policy_guid,
          body: plan.body
        })
      }
      // The status path has nowhere to record WHY, so the reason is written to
      // the policy notes. The decisions endpoint already stores it itself.
      if (rejecting && plan.kind === 'status') {
        yield call(policyNotes, {
          note: payload.reason,
          policy_guid: policy.guid,
          web_user: getUserFullname(),
          web_user_guid: getUserGuid()
        })
      }
      result = { label: policyLabel(policy), guid: policy.guid, ok: true }
    } catch (e) {
      result = {
        label: policyLabel(policy),
        guid: policy.guid,
        ok: false,
        error: errorMessageOf(e)
      }
      GAloggerException(e)
    }
    yield put(actions.approveOmkGroupPolicyResult(result))
  }

  yield put(actions.approveOmkGroupPoliciesSuccess())

  // Re-read the page the user is actually looking at, so the statuses they
  // just changed refresh under them rather than bouncing them to page 1.
  yield put(
    actions.getOmkGroupPoliciesRequest({
      guid: payload.guid,
      page: payload.page,
      limit: payload.limit
    })
  )
}

export function* approveOmkGroupPoliciesRequestWatcher() {
  yield call(
    watchRequest,
    types.APPROVE_OMK_GROUP_POLICIES_REQUEST,
    runGroupPolicyApproval,
    actions.approveOmkGroupPoliciesFailure
  )
}

// payload: { guid, docGuid }
export function* deleteOmkGroupDocumentRequestWatcher() {
  yield call(
    watchRequest,
    types.DELETE_OMK_GROUP_DOCUMENT_REQUEST,
    function*(payload) {
      const response = yield call(
        deleteOmkGroupDocument,
        payload.guid,
        payload.docGuid
      )
      yield put(actions.deleteOmkGroupDocumentSuccess(response.data))
    },
    actions.deleteOmkGroupDocumentFailure
  )
}
